import React, { useState } from 'react';
import { Car, Truck, Bus, Construction, ShoppingCart, CreditCard } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const vehicleIcons = { car: Car, truck: Truck, bus: Bus, crane: Construction };

const ProductCatalog = () => {
  const { t } = useLanguage();
  const c = t.catalog;
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [orderForm, setOrderForm] = useState({ name: '', phone: '', email: '' });
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderError, setOrderError] = useState('');

  const openModal = (product, type) => {
    setSelectedProduct(product);
    setModalType(type);
    setOrderForm({ name: '', phone: '', email: '' });
    setOrderError('');
    setShowModal(true);
  };

  const handleOrderField = (e) =>
    setOrderForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProduct) return;
    const name = orderForm.name.trim();
    const phone = orderForm.phone.trim();
    const email = orderForm.email.trim();
    if (!name || !phone || !email) {
      setOrderError('Please fill in all fields.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setOrderError('Please enter a valid email address.');
      return;
    }
    setOrderError('');
    setOrderSubmitting(true);
    const payload = {
      name,
      phone,
      email,
      order_type: modalType === 'install' ? 'install' : 'installment',
      product_id: selectedProduct.id,
      product_series: selectedProduct.series,
      product_model: selectedProduct.model,
      price: selectedProduct.price,
    };
    try {
      const res = await fetch('/api/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        console.error('Order request failed:', res.status, text);
        setOrderError('Could not send request. Please try again later.');
        return;
      }
      const data = await res.json().catch(() => ({}));
      console.log('Order submitted:', data);
      setShowModal(false);
      setOrderForm({ name: '', phone: '', email: '' });
    } catch (err) {
      console.error('Order request error:', err);
      setOrderError('Could not send request. Please try again later.');
    } finally {
      setOrderSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-[#0d0d1a] relative" id="catalog">
      <div className="absolute inset-0 cyber-stripes" />
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#ff2d95]/20 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[10px] tracking-[0.5em] text-[#ff2d95]/70 font-mono-cyber">{c.badge}</span>
          <h2 className="text-4xl md:text-5xl font-black text-white mt-4" style={{ fontFamily: 'Orbitron' }}>
            {c.title1} <span className="text-[#39ff14]">{c.title2}</span>
          </h2>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto text-sm">{c.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          {c.products.map((product) => (
            <div key={product.id}
              className="group relative bg-[#0a0a0f]/80 backdrop-blur-sm overflow-hidden hover:-translate-y-2 transition-all duration-500"
              style={{ border: '1px solid rgba(57,255,20,0.1)' }}
            >
              <div className="absolute top-0 left-0 w-4 h-[1px] bg-[#39ff14]/50" />
              <div className="absolute top-0 left-0 w-[1px] h-4 bg-[#39ff14]/50" />
              <div className="absolute bottom-0 right-0 w-4 h-[1px] bg-[#00f0ff]/50" />
              <div className="absolute bottom-0 right-0 w-[1px] h-4 bg-[#00f0ff]/50" />

              <div className="relative h-52 bg-gradient-to-b from-[#111128] to-[#0a0a0f] flex items-center justify-center p-6 overflow-hidden">
                <img src={product.image} alt={`${product.series} ${product.model}`}
                  className="h-full object-contain group-hover:scale-110 transition-transform duration-700"
                  style={{ filter: 'drop-shadow(0 0 15px rgba(57,255,20,0.1))' }} />
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1 text-[10px] font-bold tracking-[0.2em] bg-[#39ff14]/10 text-[#39ff14] border border-[#39ff14]/30" style={{ fontFamily: 'Orbitron' }}>
                    {product.series}
                  </span>
                </div>
              </div>

              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-black text-white" style={{ fontFamily: 'Orbitron', fontSize: '14px' }}>
                    {product.series} <span className="text-[#39ff14]">{product.model}</span>
                  </h3>
                  <div className="flex gap-1">
                    {product.vehicleTypes.map((type, i) => {
                      const Icon = vehicleIcons[type] || Car;
                      return <Icon key={i} size={14} className="text-white/20" />;
                    })}
                  </div>
                </div>

                <div className="space-y-1.5 mb-4 font-mono-cyber text-xs">
                  <p className="text-white/60">{product.engineVolume}</p>
                  <p className="text-white/60">{product.dimensions}</p>
                  <p className="text-[#39ff14]/85">{product.warranty}</p>
                </div>

                <div className="text-2xl font-black text-white mb-5 font-mono-cyber">{product.price}</div>

                <div className="space-y-2">
                  <button onClick={() => openModal(product, 'install')}
                    className="w-full py-3 bg-[#39ff14] hover:bg-[#39ff14]/90 text-[#0a0a0f] font-bold text-xs tracking-[0.15em] transition-all duration-300 flex items-center justify-center gap-2 cyber-clip-sm"
                    style={{ fontFamily: 'Orbitron', boxShadow: '0 0 15px rgba(57,255,20,0.2)' }}>
                    <ShoppingCart size={14} /> {c.orderBtn}
                  </button>
                  <button onClick={() => openModal(product, 'installment')}
                    className="w-full py-3 border border-[#00f0ff]/30 text-[#00f0ff] hover:bg-[#00f0ff]/5 hover:border-[#00f0ff]/60 font-bold text-xs tracking-[0.1em] transition-all duration-300 flex items-center justify-center gap-2"
                    style={{ fontFamily: 'Orbitron' }}>
                    <CreditCard size={14} /> {c.installmentBtn}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showModal && selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setShowModal(false)}>
          <div className="bg-[#0d0d1a] border border-[#39ff14]/20 p-8 max-w-md w-full mx-4"
               style={{ boxShadow: '0 0 40px rgba(57,255,20,0.1)' }}
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Orbitron', fontSize: '13px' }}>
                {modalType === 'install' ? c.modalOrder : c.modalInstallment}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-white/30 hover:text-[#ff2d95] text-2xl transition-colors">&times;</button>
            </div>
            <div className="border border-[#39ff14]/15 p-4 mb-6 bg-[#39ff14]/5">
              <p className="font-bold text-white text-sm" style={{ fontFamily: 'Orbitron' }}>{selectedProduct.series} {selectedProduct.model}</p>
              <p className="text-[#39ff14] font-bold text-xl font-mono-cyber">{selectedProduct.price}</p>
            </div>
            <form className="space-y-3" onSubmit={handleOrderSubmit}>
              <input type="text" name="name" value={orderForm.name} onChange={handleOrderField} placeholder={c.namePlaceholder}
                className="w-full px-4 py-3 bg-transparent border border-white/10 text-white placeholder-white/20 focus:outline-none focus:border-[#39ff14]/50 transition-colors text-sm" />
              <input type="tel" name="phone" value={orderForm.phone} onChange={handleOrderField} placeholder={c.phonePlaceholder}
                className="w-full px-4 py-3 bg-transparent border border-white/10 text-white placeholder-white/20 focus:outline-none focus:border-[#39ff14]/50 transition-colors text-sm" />
              <input type="email" name="email" value={orderForm.email} onChange={handleOrderField} placeholder={c.emailPlaceholder}
                className="w-full px-4 py-3 bg-transparent border border-white/10 text-white placeholder-white/20 focus:outline-none focus:border-[#39ff14]/50 transition-colors text-sm" />
              {orderError ? (
                <p className="text-xs text-[#ff2d95]" role="alert">{orderError}</p>
              ) : null}
              <button type="submit" disabled={orderSubmitting}
                className="w-full py-3 bg-[#39ff14] text-[#0a0a0f] font-bold text-xs tracking-[0.15em] cyber-clip-sm transition-all disabled:opacity-60"
                style={{ fontFamily: 'Orbitron', boxShadow: '0 0 15px rgba(57,255,20,0.2)' }}>
                {orderSubmitting ? '…' : c.submitBtn}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProductCatalog;
